import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  LinearProgress,
  Avatar,
  Checkbox,
  Button,
  Chip,
  Link,
  useTheme,
  useMediaQuery,
  Stack,
  styled,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { palette, theme as colorTheme } from "../../../Convoze.theme";



const primary = colorTheme('palette', 'primary').value;
const positive = colorTheme('palette', 'positive').value;
const neutral = colorTheme('palette', 'neutral').value;
const negative = colorTheme('palette', 'negative').value;




const TasksCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  border: "1px solid #e0e0e0",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    margin: theme.spacing(0, 1),
  },
}));


const tasks = [
  {
    id: 1,
    title: "Reduce Average Silence Duration",
    description:
      "Your silence duration has increased by 0.2 min this week. Review the recommended techniques to minimize silence during calls.",
    priority: "medium",
    priorityLabel: "Medium Priority",
    estimatedTime: "15 min",
  },
  {
    id: 2,
    title: "Improve Tone Consistency",
    description:
      "Your tone score is below average for technical support calls. Complete the tone modulation exercise with 3 example scenarios.",
    priority: "high",
    priorityLabel: "High Priority",
    estimatedTime: "20 min",
  },
  {
    id: 3,
    title: "Complete Product Knowledge Quiz",
    description:
      "New product features were released yesterday. Take the knowledge quiz to ensure you're up to date with the latest information.",
    priority: "low",
    priorityLabel: "Low Priority",
    estimatedTime: "10 min",
  },
];
const TaskItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 10,
  border: "1.5px solid #f0f0f0",
  marginBottom: theme.spacing(2),
  backgroundColor: "#fafafa",
  "&:last-child": {
    marginBottom: 0,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const StartTaskButton = styled(Button)(({ theme }) => ({
  backgroundColor: primary,
  color: "white",
  borderRadius: 8,
  padding: theme.spacing(0.3, 1.5),
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.75rem",
  "&:hover": {
    backgroundColor: primary,
  },
  // [theme.breakpoints.down("sm")]: {
  //   fontSize: "0.8rem",
  //   padding: theme.spacing(0.75, 1.5),
  // },
}));

const PriorityChip = styled(Chip)(({ theme, priority }) => {
  const getColors = (priority) => {
    switch (priority) {
      case "high":
        return {
          backgroundColor: "#ffebee",
          color: negative,
        };
      case "medium":
        return {
          backgroundColor: "#fff8e1",
          color: neutral,
        };
      case "low":
        return {
          backgroundColor: "#e8f5e8",
          color: positive,
        };
      default:
        return {
          backgroundColor: "#f5f5f5",
          color: "#666",
        };
    }
  };

  const colors = getColors(priority);

  return {
    ...colors,
    fontSize: "0.75rem",
    fontWeight: 600,
    height: 24,
    borderRadius: 4,
    "& .MuiChip-label": {
      padding: theme.spacing(0, 1),
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.7rem",
      height: 22,
    },
  };
});

function Task() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [checkedTasks, setCheckedTasks] = useState(new Set());

  const handleTaskCheck = (taskId) => {
    const newCheckedTasks = new Set(checkedTasks);
    if (newCheckedTasks.has(taskId)) {
      newCheckedTasks.delete(taskId);
    } else {
      newCheckedTasks.add(taskId);
    }
    setCheckedTasks(newCheckedTasks);
  };
  const handleStartTask = (taskId) => {
  };
  return (
    <>
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          //   justifyContent: "space-between",
          // gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h6"}
            component="h1"
            fontWeight="600"
            color="text.primary"
            sx={{ textAlign: isMobile ? "center" : "left" }}
          >
            Today's Remaining Tasks
          </Typography>
          <Link
            href="#"
            underline="none"
            sx={{
              color: primary,
              fontWeight: 500,
              fontSize: isMobile ? "0.875rem" : "1rem",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            View All
          </Link>
        </Box>
        {/* Today's Remaining Tasks Card */}
        {/* Tasks List */}
        <Stack spacing={0}>
          {tasks.map((task) => {
            const isChecked = checkedTasks.has(task?.id);

            return (
              <TaskItem key={task.id} elevation={0}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Checkbox
                    checked={isChecked}
                    onChange={() => handleTaskCheck(task?.id)}
                    sx={{
                      mt: -1,
                      "& .MuiSvgIcon-root": {
                        fontSize: isMobile ? 20 : 28,
                      },
                    }}
                  />

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      color="text.primary"
                      gutterBottom
                      sx={{
                        textDecoration: isChecked ? "line-through" : "none",
                        opacity: isChecked ? 0.6 : 1,
                        fontSize: isMobile ? "0.95rem" : "0.9rem",
                      }}
                    >
                      {task?.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        lineHeight: 1.5,
                        opacity: isChecked ? 0.6 : 1,
                        fontSize: isMobile ? "0.85rem" : "0.875rem",
                      }}
                    >
                      {task?.description}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "flex-start" : "center",
                      }}
                    >
                      <PriorityChip
                        label={task?.priorityLabel}
                        priority={task?.priority}
                        size="small"
                      />

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "text.secondary",
                        }}
                      >
                        <AccessTimeIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
                        >
                          Estimated time: {task?.estimatedTime}
                        </Typography>
                      </Box>
                    </Box>

                    <StartTaskButton
                      onClick={() => handleStartTask(task?.id)}
                      disabled={isChecked}
                      sx={{ opacity: isChecked ? 0.5 : 1 }}
                    >
                      Start Task
                    </StartTaskButton>
                  </Box>
                </Box>
              </TaskItem>
            );
          })}
        </Stack>
      </Paper>
    </>
  );
}

export default Task;
